import Loader from '@components/Loader/Loader';
import BoxSkeleton from '@components/sentinel/skeleton/BoxSkeleton/BoxSkeleton';
import VideoDescriptionSkeleton from '@components/sentinel/skeleton/VideoDescriptionSkeleton/VideoDescriptionSkeleton';
import useCurrentLang from '@hooks/useCurrentLang';
import { useStreamRespond } from '@hooks/useStreamRespond';
import { useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useAppSelector } from 'universal-scripts';
import ErrorStep from '../ErrorStep/ErrorStep';

import './ResponseStep.css';

export default function ResponseStep() {
	const verifyStatus = useAppSelector((state) => state.sentinel.phases.verify.status);
	const globalStatus = useAppSelector((state) => state.sentinel.status);
	const lang = useCurrentLang();
	const status = useAppSelector((state) => state.sentinel.phases.respond.status);
	const riskAnalysis = useAppSelector((state) => state.sentinel.phases.respond.riskAnalysis);
	const actionSuggestions = useAppSelector((state) => state.sentinel.phases.respond.actionSuggestions);
	const refreshCache = useAppSelector((state) => state.sentinel.refreshCache);

	useStreamRespond(verifyStatus, lang, refreshCache);

	const showSkeletons = status === 'processing';

	const actionsList = useMemo(() => {
		return actionSuggestions.map((action, i) => {
			const prioritizationLevel = (action.prioritization_level || 'medium').toLowerCase();
			const key = `${action.action_type}-${i}`;
			return (
				<li className="sentinel-note action-item" key={key}>
					<header className="action-header">
						<span title={prioritizationLevel} className={`priority-badge priority-${prioritizationLevel}`}>
							<span className="icon icon-fire" />
							<span>
								<FormattedMessage id={`sentinel.response.action.priority-${prioritizationLevel}`} defaultMessage={prioritizationLevel} />
							</span>
						</span>
						<span title={action.timeline} className="action-timeline">
							<strong>{action.timeline}</strong>
						</span>
					</header>

					<h3 title={action.action}>{action.action}</h3>

					<p title={action.explanation} className="action-explanation">
						{action.explanation}
					</p>

					<div className="action-details">
						<section className="detail-section">
							<h4 title={action.action_type}>
								<FormattedMessage id="sentinel.response.action.action-type" />
							</h4>
							<span title={action.action_type}>{action.action_type}</span>
						</section>
						<section className="detail-section">
							<h4 title={action.key_stakeholders}>
								<FormattedMessage id="sentinel.response.action.stakeholders" />
							</h4>
							<span title={action.key_stakeholders}>{action.key_stakeholders}</span>
						</section>
						<section className="detail-section">
							<h4 title={action.resources_required}>
								<FormattedMessage id="sentinel.response.action.resources" />
							</h4>
							<span title={action.resources_required}>{action.resources_required}</span>
						</section>
					</div>
				</li>
			);
		});
	}, [actionSuggestions]);

	if (status === 'idle') {
		if (globalStatus === 'completed' || globalStatus === 'error') {
			return null;
		}
		return <Loader />;
	}

	if (status === 'error') {
		return <ErrorStep />;
	}

	return (
		<div id="response-step" className={`sentinel-step ${status === 'completed' ? 'sentinel-step--completed' : ''}`}>
			{riskAnalysis ? (
				<div className="sentinel-note" id="risk-analysis">
					<div className="risk-metrics">
						<div className="risk-metric">
							<h3 className="metric-label">
								<FormattedMessage id="sentinel.response.risk-analysis.critical-attacks" />
							</h3>
							<span className="metric-value">{riskAnalysis.critical_attacks}</span>
						</div>
						<div className="risk-metric">
							<h3 className="metric-label">
								<FormattedMessage id="sentinel.response.risk-analysis.related-narratives" />
							</h3>
							<span className="metric-value">{riskAnalysis.related_narratives}</span>
						</div>
						<div className="risk-metric">
							<h3 className="metric-label">
								<FormattedMessage id="sentinel.response.risk-analysis.forecasted-views" />
							</h3>
							<span className="metric-value">
								<FormattedNumber value={riskAnalysis.forecasted_views} notation="compact" compactDisplay="short" />
							</span>
						</div>
						<div className="risk-metric">
							<h3 className="metric-label">
								<FormattedMessage id="sentinel.response.risk-analysis.risk-level" />
							</h3>
							<span className={`metric-value risk-level-${(riskAnalysis.risk_level || '').toLowerCase()}`}>{riskAnalysis.risk_level}</span>
						</div>
					</div>
					<div className="risk-summary">
						<h3>
							<FormattedMessage id="sentinel.response.risk-analysis.title" />
						</h3>
						<p>{riskAnalysis.summary}</p>
					</div>
				</div>
			) : (
				showSkeletons && (
					<div className="sentinel-note skeleton-box">
						<VideoDescriptionSkeleton />
					</div>
				)
			)}
			{actionSuggestions.length > 0 ? (
				<section id="action-suggestions">
					<h2>
						<FormattedMessage id="sentinel.response.action-suggestions.title" /> ({actionSuggestions.length})
					</h2>
					<ul className="actions-list">{actionsList}</ul>
				</section>
			) : (
				showSkeletons && (
					<section id="action-suggestions">
						<h2>
							<FormattedMessage id="sentinel.response.action-suggestions.title" />
						</h2>
						<ul className="actions-list">
							<li className="sentinel-note skeleton-box">
								<BoxSkeleton />
							</li>
							<li className="sentinel-note skeleton-box">
								<BoxSkeleton />
							</li>
						</ul>
					</section>
				)
			)}
		</div>
	);
}
